import { useMemo } from "react";
import type { EventOccurrence, EventType, Person } from "@shared/types";
import { chicagoDateKey, formatAgendaDayHeading, formatTime } from "@/utils/date";
import { occurrenceColor, participantBadges } from "@/utils/eventColor";
import { ParticipantBadges } from "./ParticipantBadges";
import { EmptyState } from "./EmptyState";

interface AgendaViewProps {
  events: EventOccurrence[];
  people: Person[];
  eventTypes: EventType[];
  onSelectOccurrence: (occurrence: EventOccurrence) => void;
}

/** A clean, purpose-built day-grouped list — not FullCalendar's list view. Color is used
 * sparingly (a thin accent bar per event type) instead of filling every row, so a busy week
 * stays calm and scannable instead of turning into a wall of solid color blocks. */
export function AgendaView({ events, people, eventTypes, onSelectOccurrence }: AgendaViewProps) {
  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const eventTypesById = useMemo(() => new Map(eventTypes.map((t) => [t.id, t])), [eventTypes]);

  const days = useMemo(() => {
    const groups = new Map<string, EventOccurrence[]>();
    for (const occ of events) {
      const key = chicagoDateKey(occ.occurrenceStart);
      const list = groups.get(key);
      if (list) list.push(occ);
      else groups.set(key, [occ]);
    }
    for (const list of groups.values()) {
      list.sort((a, b) => {
        if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
        return a.occurrenceStart.localeCompare(b.occurrenceStart);
      });
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  if (days.length === 0) {
    return <EmptyState title="Nothing scheduled" description="No events in this range yet." />;
  }

  return (
    <div>
      {days.map(([dayKey, dayEvents]) => (
        <section key={dayKey}>
          <h3 className="sticky top-0 z-10 border-b border-line bg-surface/95 px-4 py-2 text-sm font-semibold text-ink backdrop-blur dark:border-line-dark dark:bg-surface-dark/95 dark:text-ink-dark">
            {formatAgendaDayHeading(dayKey)}
          </h3>
          <ul className="divide-y divide-line dark:divide-line-dark">
            {dayEvents.map((occ) => (
              <li key={occ.occurrenceId}>
                <button
                  onClick={() => onSelectOccurrence(occ)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-canvas active:bg-canvas dark:hover:bg-canvas-dark dark:active:bg-canvas-dark"
                >
                  <div className="w-14 shrink-0 text-[11px] font-medium leading-tight text-muted dark:text-muted-dark">
                    {occ.allDay ? "All day" : formatTime(occ.occurrenceStart)}
                  </div>
                  <span
                    className="h-10 w-1 shrink-0 rounded-full"
                    style={{ backgroundColor: occurrenceColor(occ, eventTypesById) }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink dark:text-ink-dark">{occ.title}</p>
                    {occ.locationName && (
                      <p className="truncate text-xs text-muted dark:text-muted-dark">{occ.locationName}</p>
                    )}
                  </div>
                  <ParticipantBadges people={participantBadges(occ, peopleById)} size={22} variant="solid" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
